import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import { classNames } from "primereact/utils";
import { clearAdminSession } from "./ProtectedRoute";

const NAV = [
    { to: "/", label: "Dashboard", icon: "pi pi-th-large", end: true },
    { to: "/rides", label: "Rides", icon: "pi pi-map" },
    { to: "/map", label: "Live map", icon: "pi pi-map-marker" },
    { to: "/reports", label: "Trip reports", icon: "pi pi-chart-bar" },
    { to: "/withdrawals", label: "Withdrawal & Revenue", icon: "pi pi-wallet" },
    { to: "/coupons", label: "Coupons", icon: "pi pi-ticket" },
    { to: "/passengers", label: "Passengers", icon: "pi pi-users" },
    { to: "/drivers", label: "Drivers", icon: "pi pi-id-card" },
    { to: "/settings", label: "Settings", icon: "pi pi-cog" },
];

function SidebarNav({ onItemClick }) {
    return (
        <nav className="flex flex-column gap-1 px-2" aria-label="Main">
            {NAV.map((item) => (
                <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={onItemClick}
                    className={({ isActive }) =>
                        classNames(
                            "uc-admin-sidenav-link flex align-items-center gap-2 py-2 px-3 border-round-lg",
                            "no-underline text-sm font-medium transition-duration-150 transition-colors",
                            isActive ? "uc-admin-sidenav-link--active" : "uc-admin-sidenav-link--idle",
                        )
                    }
                >
                    <i className={classNames(item.icon, "text-base uc-admin-sidenav-icon")} aria-hidden />
                    <span>{item.label}</span>
                </NavLink>
            ))}
        </nav>
    );
}

function SignOutButton({ className = "", block = false }) {
    const navigate = useNavigate();
    return (
        <Button
            label="Sign out"
            icon="pi pi-sign-out"
            className={classNames(
                "uc-admin-sidenav-signout p-button-text p-button-sm",
                block && "w-full justify-content-start",
                className,
            )}
            onClick={() => {
                clearAdminSession();
                navigate("/login", { replace: true });
            }}
        />
    );
}

export default function AppShell({ children }) {
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <div className="uc-admin-app flex min-h-screen w-full">
            <aside
                className="uc-admin-sidenav uc-admin-sidenav--premium flex-column hidden md:flex"
                aria-label="Sidebar"
            >
                <div className="uc-admin-sidenav-brand flex align-items-center gap-3 px-3 py-3">
                    <div className="uc-admin-brand-mark flex align-items-center justify-content-center border-round-xl flex-shrink-0">
                        <i className="pi pi-shield" aria-hidden />
                    </div>
                    <div className="min-w-0">
                        <div className="font-bold text-sm line-height-1 uc-admin-sidenav-title">SWYFT Admin</div>
                        <div className="uc-admin-sidenav-tagline text-xs mt-1 line-height-1">Operations console</div>
                    </div>
                </div>
                <div className="flex-1 py-3 overflow-y-auto">
                    <SidebarNav />
                </div>
                <div className="uc-admin-sidenav-footer p-2">
                    <SignOutButton block />
                </div>
            </aside>

            <Sidebar
                visible={mobileOpen}
                position="left"
                onHide={() => setMobileOpen(false)}
                className="uc-admin-mobile-drawer uc-admin-mobile-drawer--premium w-20rem"
                header="Menu"
            >
                <SidebarNav onItemClick={() => setMobileOpen(false)} />
                <div className="mt-4 pt-3 uc-admin-mobile-drawer-footer">
                    <SignOutButton block />
                </div>
            </Sidebar>

            <div className="uc-admin-app-workspace flex flex-column flex-1 min-w-0">
                <header className="uc-admin-mobile-topbar md:hidden flex align-items-center gap-2 px-3 py-2">
                    <Button
                        type="button"
                        icon="pi pi-bars"
                        className="p-button-text p-button-rounded uc-admin-mobile-menu-btn"
                        onClick={() => setMobileOpen(true)}
                        aria-label="Open navigation"
                    />
                    <span className="font-semibold text-sm uc-admin-mobile-topbar-title">SWYFT Admin</span>
                </header>
                <main className="uc-admin-main flex-1 w-full">{children}</main>
            </div>
        </div>
    );
}
